/* eslint-disable no-console, no-use-before-define, consistent-return */
// NOTICE: KAPTION-INTEGRATION
// Kaption Feature Flags Composable - Simplified helper for checking feature flags
//
// This composable provides a simple interface to check Kaption feature flags
// without having to manually pass accountId and userId every time.
//

import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from './useAccount';

/**
 * Composable for checking Kaption Feature Flags
 *
 * @returns {Object} Object containing helper functions
 *
 * @example
 * import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
 *
 * const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
 *
 * if (isKaptionFlagEnabled('KAPTION_FEATURE_FLAGS_DEBUG')) {
 *   // Feature is enabled
 * }
 */
export function useKaptionFeatureFlags() {
  const store = useStore();
  const { accountId } = useAccount();

  // Get current user from store
  const currentUser = computed(() => store.getters.getCurrentUser);
  const userId = computed(() => currentUser.value?.id);

  /**
   * Check if a Kaption feature flag is enabled
   * Implements stale-while-revalidate: returns cached value immediately,
   * but refreshes from API in background if cache is stale
   *
   * @param {string} flagName - The name of the flag to check (e.g., 'KAPTION_FEATURE_FLAGS_DEBUG')
   * @returns {boolean} True if the flag is enabled, false otherwise
   */
  const isKaptionFlagEnabled = flagName => {
    if (!accountId.value || !userId.value) {
      return false;
    }

    // Return cached value immediately (stale-while-revalidate pattern)
    const isEnabled = store.getters['kaptionFeatureFlags/isEnabled'](
      flagName,
      accountId.value,
      userId.value
    );

    // If cache is stale, refresh in background (non-blocking)
    const valid = isCacheValid();

    if (!valid) {
      refreshFlags(false).catch(() => {
        // Silent failure
      });
    }

    return isEnabled;
  };

  /**
   * Get all cached Kaption flags for the current user
   * Implements stale-while-revalidate: returns cached flags immediately,
   * but refreshes from API in background if cache is stale
   *
   * @returns {Array} Array of cached flags
   */
  const getCachedFlags = () => {
    if (!accountId.value || !userId.value) {
      return [];
    }

    // Get cached flags immediately
    const flags = store.getters['kaptionFeatureFlags/getCachedFlags'](
      accountId.value,
      userId.value
    );

    // If cache is stale, refresh in background (non-blocking)
    const valid = isCacheValid();

    if (!valid) {
      refreshFlags(false).catch(() => {
        // Silent failure
      });
    }

    return flags;
  };

  /**
   * Check if the Kaption flags cache is valid
   *
   * @returns {boolean} True if cache is valid, false otherwise
   */
  const isCacheValid = () => {
    if (!accountId.value || !userId.value) {
      return false;
    }

    const valid = store.getters['kaptionFeatureFlags/isCacheValid'](
      accountId.value,
      userId.value
    );

    return valid;
  };

  /**
   * Manually refresh Kaption flags from the API
   *
   * @param {boolean} forceRefresh - Whether to force refresh even if cache is valid
   * @returns {Promise<Array>} Promise that resolves to the fetched flags
   */
  const refreshFlags = async (forceRefresh = false) => {
    if (!accountId.value || !userId.value) {
      return [];
    }

    return store.dispatch('kaptionFeatureFlags/fetchFlags', {
      accountId: accountId.value,
      userId: userId.value,
      platform: 'web',
      forceRefresh,
    });
  };

  /**
   * Deep reload: Clear cache and fetch fresh flags
   *
   * @returns {Promise<void>}
   */
  const deepReload = async () => {
    if (!accountId.value || !userId.value) {
      return;
    }

    return store.dispatch('kaptionFeatureFlags/deepReload', {
      accountId: accountId.value,
      userId: userId.value,
    });
  };

  // NOTICE: KAPTION-INTEGRATION - Local Override Functions

  /**
   * Set a local override for a flag (force enable/disable)
   *
   * @param {string} flagName - The name of the flag to override
   * @param {boolean} value - The override value (true = force on, false = force off)
   */
  const setLocalOverride = (flagName, value) => {
    store.dispatch('kaptionFeatureFlags/setLocalOverride', { flagName, value });
  };

  /**
   * Clear a local override for a flag (return to inherit)
   *
   * @param {string} flagName - The name of the flag to clear override for
   */
  const clearLocalOverride = flagName => {
    store.dispatch('kaptionFeatureFlags/clearLocalOverride', { flagName });
  };

  /**
   * Clear all local overrides
   */
  const clearAllLocalOverrides = () => {
    store.dispatch('kaptionFeatureFlags/clearAllLocalOverrides');
  };

  /**
   * Get all local overrides
   *
   * @returns {Object} Object with flag names as keys and override values as values
   */
  const getLocalOverrides = () => {
    return store.getters['kaptionFeatureFlags/getLocalOverrides'];
  };

  /**
   * Check if a flag has a local override
   *
   * @param {string} flagName - The name of the flag to check
   * @returns {boolean} True if the flag has a local override
   */
  const hasLocalOverride = flagName => {
    return store.getters['kaptionFeatureFlags/hasLocalOverride'](flagName);
  };

  /**
   * Get the local override value for a flag
   *
   * @param {string} flagName - The name of the flag
   * @returns {boolean|undefined} The override value, or undefined if not set
   */
  const getLocalOverride = flagName => {
    return store.getters['kaptionFeatureFlags/getLocalOverride'](flagName);
  };

  return {
    isKaptionFlagEnabled,
    getCachedFlags,
    isCacheValid,
    refreshFlags,
    deepReload,
    // Local override functions
    setLocalOverride,
    clearLocalOverride,
    clearAllLocalOverrides,
    getLocalOverrides,
    hasLocalOverride,
    getLocalOverride,
    // Expose raw values if needed
    accountId,
    userId,
  };
}
